import React, { forwardRef } from 'react';
import { Box, createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { drawerWidth } from '../../Sidebar/DrawerSection';
import { NotAllowedPage } from '../NotAllowedPage';
import { NetworkErrorPage } from './NetworkErrorPage';
import { RootState } from '../../../store';
import { LoadingPage } from './LoadingPage';
import { SettingsPageContent } from '../../Settings/SettingsPageContent';
import { useGetPortalConfigQuery } from '../../../services/api';
import { RouteContext } from '../../../context';
import { HomePage } from '../../HomePage';
import { useAppSelector } from '../../../hooks/useStore';

export interface PageContentProps {
  noSpace?: boolean;
  allowed?: boolean;
  fullMobileWidth?: boolean;
  maxWidth?: string;
  noToolbar?: boolean;
  fullPage?: boolean;
  modalWrapped?: boolean;
  isSettingsPage?: boolean;
}

const getNoSpacePadding = props => {
  if (props.modalWrapped) {
    return '52px 80px';
  }
  return 0;
};

const getLeftPadding = (props, theme: Theme, isSmScreen: boolean) => {
  if (!props.modalWrapped) {
    const additionalPadding = !isSmScreen ? drawerWidth : 0;
    return `${additionalPadding + (props.noSpace ? 0 : theme.spacing(4.5))}px`;
  }
  // modal wrapped settings page
  return `${(props.hideDrawer ? 0 : drawerWidth) +
    (props.noSpace ? 80 : theme.spacing(4.5))}px`;
};

const getMobilePadding = props => {
  if (props.modalWrapped) {
    return '52px 16px';
  }

  return '0 16px';
};

const usePageContentStyles = makeStyles(theme =>
  createStyles({
    root: {
      height: props => (props.modalWrapped ? 'auto' : '100%'),
      overflow: 'auto',
      boxSizing: 'border-box',
      [theme.breakpoints.down('xs')]: {
        padding: props => (props.noSpace ? 0 : getMobilePadding(props)),
      },
      [theme.breakpoints.up('sm')]: {
        height: '100%',
        padding: props =>
          props.noSpace ? getNoSpacePadding(props) : theme.spacing(0, 4.5),
        paddingLeft: props => getLeftPadding(props, theme, true),
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: props => getLeftPadding(props, theme, false),
      },
    },
    noSpaceContainer: {
      [theme.breakpoints.down('xs')]: {
        margin: '0 auto',
      },
    },
    noToolbar: (props: PageContentProps) => ({
      paddingTop: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingTop: props.noSpace ? 0 : theme.spacing(2),
      },
    }),
    pageContentBody: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '100%',
        padding: props => (props.modalWrapped ? '52px 0' : 'initial'),
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: props => (props.modalWrapped ? '70%' : '100%'),
      },
      width: '100%',
      height: props => (props.modalWrapped ? 'auto' : '100%'),
      display: 'flex',
      margin: props => (props.modalWrapped ? '0 auto' : 'initial'),
      flexDirection: 'column',
    },
    fullPage: {
      padding: 0,
    },
  }),
);

export const PageContent = forwardRef<HTMLDivElement, PageContentProps>(
  (
    {
      children,
      allowed = true,
      noToolbar = false,
      fullPage = false,
      modalWrapped = false,
      isSettingsPage = false,
      ...styleProps
    },
    ref,
  ) => {
    // all hookes should be defined before any return to avoid:
    // Error: Rendered more hooks than previous render
    const userLoaded = useSelector((state: RootState) => state.user.loaded);
    const {
      isLoading: isPortalConfigurationLoading,
    } = useGetPortalConfigQuery(); // Check whether portal configuration is loaded
    const loadingError = useSelector(
      (state: RootState) => state.user.loadingError,
    );
    const { pathname } = React.useContext(RouteContext);
    const isClientUser = useAppSelector(state => state.user.isClient);
    const classes = usePageContentStyles({
      ...styleProps,
      noToolbar,
    });
    const redirectToHomePage =
      (pathname === '/' || pathname.startsWith('/portal/')) && !isClientUser;

    let PageOverrideComponent;
    if (loadingError) {
      PageOverrideComponent = NetworkErrorPage;
    } else if (!userLoaded || isPortalConfigurationLoading) {
      PageOverrideComponent = LoadingPage;
    } else if (!allowed) {
      PageOverrideComponent = NotAllowedPage;
    }

    const noSpaceContainerProps = {
      className: classNames(classes.root, classes.noSpaceContainer, {
        [classes.fullPage]: noToolbar,
      }),
      width: 1,
      ref,
    };

    if (PageOverrideComponent) {
      return (
        <Box {...noSpaceContainerProps} display="flex">
          <PageOverrideComponent />
        </Box>
      );
    }

    if (isSettingsPage) {
      return <SettingsPageContent>{children}</SettingsPageContent>;
    }

    if (fullPage) {
      return (
        <Box className={classes.fullPage} width={1} ref={ref}>
          {children}
        </Box>
      );
    }

    if (styleProps.noSpace) {
      return <Box {...noSpaceContainerProps}>{children}</Box>;
    }
    return (
      <Grid
        container
        className={classNames(classes.root, {
          [classes.noToolbar]: noToolbar,
        })}
        ref={ref}
      >
        <Grid className={classes.pageContentBody} item>
          {redirectToHomePage ? <HomePage /> : children}
        </Grid>
      </Grid>
    );
  },
);
